import React, { useState } from "react";
import Image from "next/image";
import styles from "../../styles/shap_of_you.module.scss";
import SOYshp from "../../assets/images/image_sha.png";
import { FaceResultScreenProps } from "./types";

const FaceResultScreen: React.FC<FaceResultScreenProps> = ({
  navigateToStep,
  isAuthenticated,
  faceShape,
  complexion,
  capturedImage,
  captureMethod,
  onReupload,
  onExploreJewellery,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleExploreClick = () => {
    // If we have onExploreJewellery callback from PhotoCaptureFlow, use it
    if (onExploreJewellery) {
      onExploreJewellery();
    } else {
      // Fallback: If user is already logged in, go directly to productView
      // Otherwise, go to login screen
      if (isAuthenticated) {
        navigateToStep("productView");
      } else {
        navigateToStep("login");
      }
    }
  };

  const handleReuploadClick = () => {
    // If we have onReupload callback from PhotoCaptureFlow, use it
    if (onReupload) {
      onReupload();
    } else {
      // Fallback: navigate to uploadOrScan
      navigateToStep("uploadOrScan");
    }
  };

  return (
    <div className="d-md-none w-100">
      <div className={styles.wrapper_reupload}>
        {/* Background Image */}
        <div className={styles.image}>
          {capturedImage ? (
            <img src={capturedImage} alt="Result" className={styles.bg} />
          ) : (
            <Image src={SOYshp} alt="Result" className={styles.bg} />
          )}
        </div>

        {/* Face Information Card */}
        <div className={styles.faceInfoCard}>
          <div
            className={styles.toggleButton}
            onClick={toggleExpand}
            style={{ cursor: "pointer" }}
          >
            {!isExpanded ? (
              // Up arrow SVG (collapsed state)
              <div className={styles.svg_mark}>
                <svg
                  width="18"
                  height="14"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.96094 5.5L8.80471 1.5L14.6485 5.5"
                    stroke="white"
                    strokeOpacity="0.31"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1.5 12.25L8.80471 7.25L16.1094 12.25"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : (
              // Down arrow SVG (expanded state)
              <svg
                width="173"
                height="36"
                viewBox="0 0 173 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <foreignObject x="-6" y="-5.99998" width="185" height="48">
                  <div
                    style={{
                      backdropFilter: "blur(3px)",
                      clipPath: "url(#bgblur_0_4559_2381_clip_path)",
                      height: "100%",
                      width: "100%",
                    }}
                  ></div>
                </foreignObject>
                <g data-figma-bg-blur-radius="6">
                  <path
                    d="M92.4653 20L86.4998 24L80.5343 20"
                    stroke="white"
                    strokeOpacity="0.31"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M93.957 13.25L86.5001 18.25L79.0432 13.25"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath
                    id="bgblur_0_4559_2381_clip_path"
                    transform="translate(6 5.99998)"
                  >
                    <path d="M173 24C173 30.6274 167.627 36 161 36L12 36C5.37259 36 4.69686e-07 30.6274 1.04907e-06 24L2.09815e-06 12C2.67753e-06 5.37258 5.37259 4.69686e-07 12 1.04907e-06L161 1.40751e-05C167.627 1.46545e-05 173 5.3726 173 12L173 24Z" />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className={`${styles.faceInfoContentContainer} ${
              isExpanded ? styles.expanded : styles.collapsed
            }`}
          >
            <div className={styles.faceInfoContent}>
              <div className={styles.faceInfoLeft}>
                <div className={styles.faceTypeValue}>
                  {faceShape || "Unknown"}
                </div>
                <div className={styles.faceTypeLabel}>Face Type</div>
              </div>
              <div className={styles.faceInfoDivider}></div>
              <div className={styles.faceInfoRight}>
                <div className={styles.skinToneValue}>
                  {complexion || "Unknown"}
                </div>
                <div className={styles.skinToneLabel}>Skin Tone</div>
              </div>
            </div>
            <div className={styles.faceTypeDescription}>
              {faceShape && (() => {
                const descriptions: Record<string, string> = {
                  Heart: "Accentuate your delicate chin with elegant drops and dangles that add softness and refined symmetry.",
                  Oblong: "Enhance your face with voluminous chandeliers and full drops that add width and harmonious balance.",
                  Triangle: "Featuring an elegant taper from forehead to chin, bold drop styles add volume where it's needed most.",
                  Oval: "Perfectly proportioned and endlessly versatile, every jewellery style, from contemporary to statement, complements you beautifully.",
                  Round: "Celebrate your soft curves with striking, angular jewellery that adds beautiful contrast and definition.",
                  Square: "Balance your strong jawline with fluid drops and pear-shaped designs that soften your look with effortless elegance.",
                };
                return (
                  <>
                    <strong>Your Face Shape is {faceShape}</strong>
                    <br />
                    {descriptions[faceShape] ?? `${faceShape} faces are balanced and versatile`}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Bottom Buttons */}
        <div className={styles.actions}>
          <button className={styles.explore} onClick={handleExploreClick}>
            Explore Jewellery →
          </button>

          <button className={styles.reupload} onClick={handleReuploadClick}>
            {captureMethod === "scan" ? "Rescan" : "Reupload Image"}
            <span>
              {captureMethod === "scan" ? (
                <i className="bi bi-arrow-clockwise"></i>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8H15.01"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.5 21H6C5.20435 21 4.44129 20.6839 3.87868 20.1213C3.31607 19.5587 3 18.7956 3 18V6C3 5.20435 3.31607 4.44129 3.87868 3.87868C4.44129 3.31607 5.20435 3 6 3H18C18.7956 3 19.5587 3.31607 20.1213 3.87868C20.6839 4.44129 21 5.20435 21 6V12.5"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 15.9998L8 10.9998C8.928 10.1068 10.072 10.1068 11 10.9998L14.5 14.4998"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 13.9999L15 12.9999C15.679 12.3469 16.473 12.1709 17.214 12.4739"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 22V16"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 19L19 16L16 19"
                    stroke="#262121"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaceResultScreen;
